/* eslint-disable max-len */
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import ReactMap, {
    FullscreenControl,
    GeolocateControl,
    Layer,
    MapLayerMouseEvent,
    MapRef,
    Marker,
    NavigationControl,
    Popup,
    ScaleControl,
    Source,
    ViewStateChangeEvent,
} from 'react-map-gl/maplibre';
import { LngLatBounds, Offset } from 'maplibre-gl';
import { Feature, FeatureCollection, Point, Position } from 'geojson';

import { SvgReactComponent as SvgEvent } from './svg/Event';
import { SvgReactComponent as SvgNews } from './svg/News';
import { SvgReactComponent as SvgPlaylist } from './svg/Playlist';
import { SvgReactComponent as SvgPodcast } from './svg/Podcast';
import { SvgReactComponent as SvgPerformer } from './svg/Performer';

import 'maplibre-gl/dist/maplibre-gl.css';
import './sun-map.css';

const IGN_ORTHO_TILES_URL = 'https://data.geopf.fr/wmts?' + [
    'SERVICE=WMTS',
    'REQUEST=GetTile',
    'VERSION=1.0.0',
    'LAYER=ORTHOIMAGERY.ORTHOPHOTOS',
    'FORMAT=image/jpeg',
    'STYLE=normal',
    'TILEMATRIXSET=PM',
    'TILEMATRIX={z}',
    'TILEROW={y}',
    'TILECOL={x}',
].join('&');

declare namespace Livewire {
    function dispatch(eventName: string, params?: Record<string, any>): void;
    function dispatchTo(componentName: string, eventName: string, params?: Record<string, any>): void;
}

declare global {
    interface WindowEventMap {
        'map:draw': CustomEvent<MapData>;
        'map:init': CustomEvent<MapData>;
        'map:move': CustomEvent<{ position: Position, zoom?: number }>;
        'map-preview:init': CustomEvent<MapPreviewData>;
        'userLogin': CustomEvent<{ userTheme: string }>;
    }
}

type ContentType = 'event' | 'news' | 'performer' | 'playlist' | 'podcast';
type LocationCollection = FeatureCollection<Point, LocationProperties>;

interface MapStyle {
    [key: string]: string;
    name: string,
    theme: 'both' | 'dark' | 'light',
}

interface MapData {
    [key: string]: any;
    content: LocationCollection | null,
    settings: {
        activePlaceId?: number,
        colorScheme?: string | null,
        fullscreenContainerId?: string,
        mapContainerId?: string,
        mapStyle?: MapStyle;
        position?: Position,
        searchMode?: 'all' | ContentType,
        zoom?: number,
    },
}

interface MapSettings {
    [key: string]: any;
    colorScheme: string;
    fullscreenContainerId: string | null;
    mapStyle: MapStyle;
    searchMode: 'all' | ContentType;
}

interface LocationProperties {
    [key: string]: any;
    //cacheKey: string;
    id: number;
    label: string;
    type: string;
    //picture?: string;
    contentType?: 'multiple' | ContentType;
    contentCounts?: Record<ContentType, number>;
    todaysContentCount?: number;
}

interface MapPreviewPlace {
    id: number;
    name: string;
    position: Position;
}

interface MapPreviewData {
    mapContainerId: string;
    places: MapPreviewPlace[];
    zoom?: number;
    mapStyle?: MapStyle;
    colorScheme?: string | null;
    contentType?: ContentType;
    contentId?: number;
}

const resolveColorScheme = (preferredColorScheme?: string | null): string => {
    let colorScheme = localStorage.getItem('userTheme') ?? preferredColorScheme ?? 'auto';
    if ('auto' === colorScheme) {
        colorScheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    return colorScheme;
};

const resolveIconColor = (mapStyle: MapStyle, colorScheme: string): string => {
    if ('both' === mapStyle.theme) {
        return 'dark' === colorScheme ? 'yellow' : 'blue';
    }

    return 'dark' === mapStyle.theme ? 'yellow' : 'blue';
};

// Racines React des cartes montées, indexées par conteneur. La navigation
// étant gérée côté Livewire (sans rechargement de page), les conteneurs sont
// retirés du DOM sans que React ne soit prévenu : les racines orphelines
// doivent être démontées explicitement pour libérer les instances MapLibre
// (et leurs contextes WebGL, limités en nombre par le navigateur) ainsi que
// les écouteurs d'évènements posés sur window.
const mapRoots = new Map<HTMLElement, Root>();

const mountMapRoot = (container: HTMLElement, element: React.ReactNode): void => {
    mapRoots.forEach((root, rootContainer) => {
        if (!rootContainer.isConnected || rootContainer === container) {
            root.unmount();
            mapRoots.delete(rootContainer);
        }
    });

    const root = createRoot(container);
    root.render(element);
    mapRoots.set(container, root);
};

// The "map:init" event is intended to initialize of the SunMap component,
// that's obviously why it is the only one handled outside the component.
// All other map related events are handled inside the component using the
// useEffect() hook.
window.addEventListener('map:init', (event: CustomEvent<MapData>): void => {
    const settings = event.detail.settings;
    if (!settings.mapContainerId) {
        alert("Une erreur est survenue à l'affichage de la carte. Veuillez réessayer ultérieurement.");
        console.error('Missing required setting "mapContainerId".');

        return;
    }

    const container = document.getElementById(settings.mapContainerId) as HTMLElement | null;
    if (null === container) {
        alert("Une erreur est survenue à l'affichage de la carte. Veuillez réessayer ultérieurement.");
        console.error(`No element identified by "${settings.mapContainerId}".`);

        return;
    }

    mountMapRoot(container, <SunMap settings={settings} content={event.detail.content}/>);
});

// The "map-preview:init" event mounts a lightweight, non-interactive map
// instance (e.g. a location preview on a content detail page), reusing the
// same styles as the "real" map but without any content layer or control.
window.addEventListener('map-preview:init', (event: CustomEvent<MapPreviewData>): void => {
    const { mapContainerId } = event.detail;
    if (!mapContainerId) {
        console.error('Missing required property "mapContainerId".');

        return;
    }

    const container = document.getElementById(mapContainerId) as HTMLElement | null;
    if (null === container) {
        console.error(`No element identified by "${mapContainerId}".`);

        return;
    }

    mountMapRoot(container, <MapPreview {...event.detail}/>);
});

const getBounds = (positions: Position[]): LngLatBounds => {
    const bounds = new LngLatBounds();
    positions.forEach(position => bounds.extend(position as [number, number]));

    return bounds;
};

const MapPreview = ({
    places,
    zoom = 10,
    mapStyle = { name: 'default', theme: 'both' },
    colorScheme,
    contentType = 'event',
    contentId,
}: MapPreviewData) => {
    const mapRef = useRef<MapRef | null>(null);
    const resolvedColorScheme = resolveColorScheme(colorScheme);

    const styleUrl = 'both' === mapStyle.theme
        ? `/data/styles/${mapStyle.name}-${resolvedColorScheme}.json`
        : `/data/styles/${mapStyle.name}.json`;

    const positions = places.map(place => place.position);
    // Extra bottom padding keeps markers clear of the "view on map" button
    // overlaid at the bottom of the minimap.
    const initialViewState = positions.length > 1
        ? {
            bounds: getBounds(positions),
            fitBoundsOptions: { padding: { top: 40, bottom: 40, left: 24, right: 24 }, maxZoom: 10 },
        }
        : { longitude: positions[0][0], latitude: positions[0][1], zoom };

    const iconColor = resolveIconColor(mapStyle, resolvedColorScheme);
    const iconUrl = `/images/icons/location-${contentType}-${iconColor}.svg`;

    const onPlaceClick = useCallback((place: MapPreviewPlace) => {
        Livewire.dispatchTo('router', 'nav:to', {
            routeKey: 'map',
            params: {
                place: place.id,
                place_show_all: 1,
                lng: place.position[0],
                lat: place.position[1],
                zoom: 14,
            },
        });
    }, []);

    const onViewOnMapClick = useCallback(() => {
        if (1 === places.length) {
            onPlaceClick(places[0]);

            return;
        }

        const map = mapRef.current;
        const center = map ? map.getCenter() : { lng: positions[0][0], lat: positions[0][1] };
        let targetZoom = map ? map.getZoom() : zoom;

        // The minimap's fitBounds zoom is computed for its own (small) container.
        // Reused as-is on the full-size map, the same zoom level would reveal a
        // much larger area, so it's bumped up by the container-size ratio.
        if (map) {
            const container = map.getContainer();
            const zoomCorrection = Math.log2(Math.min(
                window.innerWidth / container.clientWidth,
                window.innerHeight / container.clientHeight
            ));
            if (Number.isFinite(zoomCorrection) && zoomCorrection > 0) {
                targetZoom = Math.min(targetZoom + zoomCorrection, 16);
            }
        }

        Livewire.dispatchTo('router', 'nav:to', {
            routeKey: 'map',
            params: {
                lng: center.lng,
                lat: center.lat,
                zoom: targetZoom,
                ...('event' === contentType && contentId
                    ? { search: 'evenement:' + contentId, mode: contentType }
                    : {}),
            },
        });
    }, [places, positions, zoom, onPlaceClick, contentType, contentId]);

    return (
        <div className="map-preview-container">
            <ReactMap
                ref={mapRef}
                initialViewState={initialViewState}
                mapStyle={styleUrl}
                dragPan={false}
                scrollZoom={false}
                doubleClickZoom={false}
                dragRotate={false}
                touchZoomRotate={false}
                touchPitch={false}
                boxZoom={false}
                keyboard={false}
                attributionControl={false}
            >
                {places.map(place => (
                    <Marker
                        key={place.id}
                        longitude={place.position[0]}
                        latitude={place.position[1]}
                        anchor="bottom"
                        onClick={() => onPlaceClick(place)}
                    >
                        <img
                            src={iconUrl}
                            alt={place.name}
                            title={place.name}
                            width={30}
                            height={36}
                            className="map-preview-marker-icon"
                        />
                    </Marker>
                ))}
            </ReactMap>
            <button
                type="button"
                className="map-preview-view-button"
                onClick={onViewOnMapClick}
            >
                Visualiser sur la carte
            </button>
        </div>
    );
};

const SunMap = ({ content = null, settings: defaultSettings = {} }: MapData) => {
    const mapRef = useRef<MapRef | null>(null);

    const [settings, setSettings] = useState((): MapSettings => ({
        colorScheme: resolveColorScheme(defaultSettings.colorScheme),
        fullscreenContainerId: defaultSettings.fullscreenContainerId ?? null,
        mapStyle: defaultSettings.mapStyle ?? { name: 'default', theme: 'both' },
        searchMode: defaultSettings.searchMode ?? 'all',
    }));

    const [viewState, setViewState] = useState({
        longitude: defaultSettings.position?.at(0) ?? -1.561013,
        latitude: defaultSettings.position?.at(1) ?? 47.219074,
        zoom: defaultSettings.zoom ?? 10,
    });

    const [activePlaceId, setActivePlaceId] = useState<number | null>(defaultSettings.activePlaceId ?? null);
    const [popupData, setPopupData] = useState<Feature<Point, LocationProperties> | null>(null);
    const [cursor, setCursor] = useState<string>('auto');
    const [geoData, setGeoData] = useState<LocationCollection | null>(null);
    const [imagesLoaded, setImagesLoaded] = useState<boolean>(false);
    const [showOrtho, setShowOrtho] = useState<boolean>(false);

    // Handle "map:draw" event.
    useEffect(() => {
        const handleMapDrawEvent = (event: CustomEvent<MapData>) => {
            const eventSettings = event.detail.settings;
            let eventContent = event.detail.content;
            if (eventSettings.position) {
                setViewState(prevState => {
                    return {
                        longitude: (eventSettings.position as Position)[0],
                        latitude: (eventSettings.position as Position)[1],
                        zoom: eventSettings.zoom ?? prevState.zoom,
                    };
                });
            }

            const newSettings: any = {};
            if (eventSettings.mapStyle) {
                newSettings.mapStyle = eventSettings.mapStyle;
                setupImages(newSettings.mapStyle, settings.colorScheme);
            }
            if (eventSettings.searchMode) {
                newSettings.searchMode = eventSettings.searchMode;
            }
            if (Object.keys(newSettings).length) {
                setSettings({...settings, ...newSettings});
            }

            if (eventContent) {
                eventContent = prepareFeatures(
                    eventContent,
                    eventSettings.searchMode ?? settings.searchMode
                );
            }

            setGeoData(eventContent);

            if (activePlaceId) {
                let forgetActivePlace: boolean = (
                    (eventSettings.searchMode && 'all' !== eventSettings.searchMode) ||
                    (!eventSettings.searchMode && 'all' !== settings.searchMode)
                );

                let matchingFeature = null;

                if (!forgetActivePlace) {
                    if (!eventContent?.features) {
                        forgetActivePlace = true;
                    } else {
                        matchingFeature = eventContent.features.find((feature) => {
                            return feature.properties.id === activePlaceId;
                        }) ?? null;

                        forgetActivePlace = !matchingFeature || 'multiple' !== matchingFeature.properties.contentType;
                    }
                }

                if (forgetActivePlace) {
                    setActivePlaceId(null);
                    Livewire.dispatch('map:place:selected', {placeId: null});
                    if (popupData) {
                        setPopupData(null);
                    }
                } else {
                    // Les propriétés du lieu actif ont probablement changé
                    // dans les nouvelles données reçues, on met donc à jour
                    // le state popupData avec la nouvelle feature.
                    setPopupData(matchingFeature);
                }
            }
        };

        window.addEventListener('map:draw', handleMapDrawEvent);

        return () => {
            window.removeEventListener('map:draw', handleMapDrawEvent);
        }
    }, [activePlaceId, popupData, settings]);

    // Handle "map:move" event.
    useEffect(() => {
        const handleMapMoveEvent = (event: CustomEvent<{ position: Position, zoom?: number }>) => {
            setViewState(prevState => {
                return {
                    longitude: event.detail.position[0],
                    latitude: event.detail.position[1],
                    zoom: event.detail.zoom ?? prevState.zoom,
                };
            });
        };

        window.addEventListener('map:move', handleMapMoveEvent);

        return () => {
            window.removeEventListener('map:move', handleMapMoveEvent);
        }
    }, []);

    // Handle user login.
    useEffect(() => {
        const handleUserLogin = (event: CustomEvent<{ userTheme: string }>) => {
            let colorScheme = event.detail.userTheme;
            if ('auto' === colorScheme) {
                const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
                colorScheme = mediaQuery.matches ? 'dark' : 'light';
            }

            setupImages(settings.mapStyle, colorScheme);
            setSettings({...settings, colorScheme: colorScheme});
        };

        window.addEventListener('userLogin', handleUserLogin);

        return () => {
            window.removeEventListener('userLogin', handleUserLogin);
        };
    }, [settings]);

    // Handle preferred color scheme change.
    useEffect(() => {
        const handleColorSchemeChange = (event: MediaQueryListEvent) => {
            const storedColorScheme = localStorage.getItem('userTheme');
            if (storedColorScheme && 'auto' !== storedColorScheme) {
                return;
            }

            const colorScheme = event.matches ? 'dark' : 'light';
            setupImages(settings.mapStyle, colorScheme);
            setSettings({...settings, colorScheme: colorScheme});
        };

        const darkSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
        darkSchemeQuery.addEventListener('change', handleColorSchemeChange);

        return () => {
            darkSchemeQuery.removeEventListener('change', handleColorSchemeChange);
        }
    }, [settings]);

    const prepareFeatures = useCallback((collection: LocationCollection, searchMode: 'all' | ContentType): LocationCollection => {
        if ('all' === searchMode) {
            collection.features.forEach(feature => {
                if (!feature.properties.contentCounts) {
                    feature.properties.contentType = 'multiple';

                    return;
                }

                let contentType: ContentType;
                let featureContentType: ContentType | 'multiple' | null = null;

                for (contentType in feature.properties.contentCounts) {
                    const value = feature.properties.contentCounts[contentType];
                    if (value > 0) {
                        if (null === featureContentType) {
                            featureContentType = contentType;
                        } else {
                            featureContentType = 'multiple';
                            break;
                        }
                    }
                }

                feature.properties.contentType = featureContentType ?? 'multiple';
            });
        } else {
            collection.features.forEach(feature => {
                feature.properties.contentType = searchMode as ContentType;
            });
        }

        return collection;
    }, []);

    const setupImages = useCallback((mapStyle: MapStyle, colorScheme: string): Promise<void> => {
        const map = mapRef.current;
        if (!map) {
            return Promise.resolve();
        }

        const iconColor = resolveIconColor(mapStyle, colorScheme);

        const iconTypes = ['default', 'event', 'news', 'performer', 'playlist', 'podcast'];

        return Promise.all(iconTypes.map((type: string) => new Promise<void>((resolve) => {
            const image = new Image(30, 36);
            image.onload = () => {
                // La carte a pu être démontée pendant le chargement.
                if (!mapRef.current) {
                    resolve();

                    return;
                }
                if (map.hasImage(type)) {
                    map.removeImage(type);
                }
                map.addImage(type, image, { sdf: false });
                resolve();
            };
            image.onerror = () => {
                console.error(`Unable to load map icon "${image.src}".`);
                resolve();
            };
            image.src = `/images/icons/location-${type}-${iconColor}.svg`;
        }))).then(() => undefined);
    }, []);

    const getMapStyle = useCallback((): string => {
        if ('both' === settings.mapStyle.theme) {
            return `/data/styles/${settings.mapStyle.name}-${settings.colorScheme}.json`;
        }

        return `/data/styles/${settings.mapStyle.name}.json`;
    }, [settings.colorScheme, settings.mapStyle]);

    // Les icônes sont ajoutées dès la création de l'instance MapLibre, sans
    // attendre l'évènement "load" : celui-ci n'est émis qu'une fois toutes les
    // tuiles du fond de carte téléchargées, ce qui retarde l'affichage des
    // lieux quand le service de tuiles est lent.
    const setMapRef = useCallback((ref: MapRef | null) => {
        const isNewMap = null !== ref && null === mapRef.current;
        mapRef.current = ref;

        if (isNewMap) {
            setupImages(settings.mapStyle, settings.colorScheme).then(() => setImagesLoaded(true));
        }
    }, []);

    // Chargement des lieux initiaux.
    useEffect(() => {
        if (content) {
            const geoData = prepareFeatures(content, settings.searchMode);

            setGeoData(geoData);

            if (defaultSettings.activePlaceId && geoData?.features.length) {
                const matchingFeature = geoData.features.find((feature) => {
                    return feature.properties.id === defaultSettings.activePlaceId
                });

                if (matchingFeature) {
                    setPopupData(matchingFeature);
                }
            }
        }
    }, []);

    const onClick = useCallback((event: MapLayerMouseEvent) => {
        if (event.features && event.features.length > 0) {
            const clickedFeature = event.features[0];

            // Au sein des features remontées par l'évènement, les propriétés
            // de type objet peuvent avoir été transformées en JSON.
            if (typeof clickedFeature.properties.contentCounts === 'string') {
                clickedFeature.properties.contentCounts = JSON.parse(clickedFeature.properties.contentCounts);
            }

            if ('all' === settings.searchMode && 'multiple' === clickedFeature.properties.contentType) {
                if (!popupData || clickedFeature.properties.id !== activePlaceId) {
                    setPopupData({
                        type: clickedFeature.type,
                        properties: clickedFeature.properties as LocationProperties,
                        geometry: clickedFeature.geometry as Point,
                    });
                }

                if (clickedFeature.properties.id !== activePlaceId) {
                    setActivePlaceId(clickedFeature.properties.id);
                    Livewire.dispatch('map:place:selected', {placeId: clickedFeature.properties.id});
                    Livewire.dispatch('map:list:hide');
                }
            } else {
                if (!popupData || clickedFeature.properties.id !== activePlaceId) {
                    setPopupData({
                        type: clickedFeature.type,
                        properties: clickedFeature.properties as LocationProperties,
                        geometry: clickedFeature.geometry as Point,
                    });
                }
                if (clickedFeature.properties.id !== activePlaceId) {
                    setActivePlaceId(clickedFeature.properties.id);
                    Livewire.dispatch('map:place:selected', {placeId: clickedFeature.properties.id});
                }
                Livewire.dispatch(
                    'map:list:show',
                    {
                        contentType: clickedFeature.properties.contentType,
                        placeId:clickedFeature.properties.id
                    }
                );
            }

            if (clickedFeature.properties.id !== activePlaceId) {
                Livewire.dispatch('map:info:hide');
            }
        } else {
            if (popupData) {
                setPopupData(null);
            }
            setActivePlaceId(null);
            Livewire.dispatch('map:place:selected', {placeId: null});
            Livewire.dispatch('map:info:hide');
            Livewire.dispatch('map:list:hide');
        }
    }, [activePlaceId, popupData, settings.searchMode]);

    const onMouseEnter = useCallback(() => setCursor('pointer'), []);
    const onMouseLeave = useCallback(() => setCursor('auto'), []);

    return (
        <ReactMap
            ref={setMapRef}
            {...viewState}
            minZoom={5}
            maxZoom={17}
            interactiveLayerIds={['place_icons', 'place_icons_highlights']}
            cursor={cursor}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onMove={(event: ViewStateChangeEvent) => setViewState(event.viewState)}
            onMoveEnd={(event: ViewStateChangeEvent) => Livewire.dispatch('map:view-state', {viewState: event.viewState})}
            mapStyle={getMapStyle()}
            dragRotate={false}
            touchZoomRotate={true}
            touchPitch={false}
        >
            <ScaleControl position="top-left"/>
            <NavigationControl position="top-left"/>
            <GeolocateControl position="top-left"/>
            {settings.fullscreenContainerId && (
                <FullscreenControl containerId={settings.fullscreenContainerId} position="top-left"/>
            )}

            <Source
                id="ortho"
                type="raster"
                tileSize={256}
                tiles={[IGN_ORTHO_TILES_URL]}
            >
                {showOrtho && (
                    <Layer
                        id="ortho-layer"
                        type="raster"
                        beforeId="outline"
                    />
                )}
            </Source>

            <Source id="france" type="geojson" data={'/data/regions.geojson'}>
                <Layer
                    id="outline"
                    type='fill'
                    paint={{
                        'fill-color': 'rgba(0, 0, 0, 0.2)'
                    }}
                    filter={['!=', 'code', '52']}
                />
            </Source>

            {/* Les lieux ne sont affichés qu'une fois les icônes disponibles,
                faute de quoi MapLibre place les symboles sans elles. */}
            {geoData && imagesLoaded && (
                <Source id="sunradio" type="geojson" data={geoData}>
                    <Layer
                        id="place_icons"
                        type="symbol"
                        layout={{
                            'icon-image': [
                                'case',
                                ['==', ['get', 'contentType'], 'multiple'],
                                'default',
                                ['get', 'contentType']
                            ],
                            'icon-allow-overlap': true,
                            'icon-anchor': 'bottom',
                        }}
                        filter={['==' , 0, ['get', 'todaysContentCount']]}
                    />
                    <Layer
                        id="place_icons_highlights"
                        type="symbol"
                        layout={{
                            'icon-image': [
                                'case',
                                ['==', ['get', 'contentType'], 'multiple'],
                                'default',
                                ['get', 'contentType']
                            ],
                            'icon-allow-overlap': true,
                            'icon-anchor': 'bottom',
                        }}
                        filter={['<' , 0, ['get', 'todaysContentCount']]}
                    />
                    <Layer
                        id='today_highlights'
                        type='circle'
                        paint={{
                            'circle-color': 'junior' === settings.mapStyle.name ? '#ffcd00' : '#ea5c2f',
                            'circle-radius': 6,
                            'circle-translate' : [-11, -30]
                        }}
                        filter={['<' , 0, ['get', 'todaysContentCount']]}
                    />
                    <Layer
                        id="place_labels"
                        type="symbol"
                        layout={{
                            'text-field': ['get', 'label'],
                            'text-font': ['Open Sans Bold'],
                            'text-size': 15,
                            'text-anchor': 'left',
                            'text-offset': [1.5,-1.3],
                            'text-allow-overlap': false,
                            'symbol-sort-key': [
                                'case',
                                ['>', ['get', 'todaysContentCount'], 0],
                                0,
                                1
                            ]
                        }}
                        paint={{
                            'text-color': (settings.colorScheme === 'dark' && 'junior' !== settings.mapStyle.name) ? '#FFCD00' : '#004266',
                            'text-halo-color': 'junior' === settings.mapStyle.name ? '#be5c3d' : (settings.colorScheme === 'dark' ? '#141414' : '#e0eef2'),
                            'text-halo-width': 1,
                        }}
                        filter={[
                            'all',
                            ['>=', ['zoom'], 12],
                            ['!=', ['get', 'id'], activePlaceId || -1]
                        ]}
                    />
                </Source>
            )}

            {/* Content Popup */}
            {popupData && (
                popupData.properties.contentType === 'multiple' ? (
                    <Popup
                        anchor="center"
                        offset={[0, -18] as Offset}
                        longitude={popupData.geometry.coordinates[0]}
                        latitude={popupData.geometry.coordinates[1]}
                        closeButton={false}
                        closeOnClick={false}
                    >
                        <button className="popup-label"
                            onClick={() => Livewire.dispatchTo(
                                'router',
                                'nav:to',
                                {
                                    routeKey: 'place_details',
                                    params: {
                                        bindings: {
                                            place: {
                                                model: 'App\\Models\\Map\\Place',
                                                id: popupData.properties.id
                                            }
                                        }
                                    }
                                }
                            )}
                        >
                            {popupData.properties.label}
                        </button>
                        <button
                            className="circle-button pos-north"
                            title={popupData.properties.contentCounts?.event ? "Voir les évènements" : "Aucun évènement"}
                            onClick={() => Livewire.dispatch('map:list:show', {contentType: 'event', placeId: popupData.properties.id})}
                            disabled={!popupData.properties.contentCounts?.event}
                        >
                            <SvgEvent/>
                        </button>
                        <button
                            className="circle-button pos-1-5th"
                            title={popupData.properties.contentCounts?.news ? "Voir les actualités" : "Aucune actualité"}
                            onClick={() => Livewire.dispatch('map:list:show', {contentType: 'news', placeId: popupData.properties.id})}
                            disabled={!popupData.properties.contentCounts?.news}
                        >
                            <SvgNews/>
                        </button>
                        <button
                            className="circle-button pos-4-5th"
                            title={popupData.properties.contentCounts?.performer ? "Voir les artistes" : "Aucun artiste"}
                            onClick={() => Livewire.dispatch('map:list:show', {contentType: 'performer', placeId: popupData.properties.id})}
                            disabled={!popupData.properties.contentCounts?.performer}
                        >
                            <SvgPerformer/>
                        </button>
                        <button
                            className="circle-button pos-2-5th"
                            title={popupData.properties.contentCounts?.playlist ? "Voir les playlists" : "Aucune playlist"}
                            onClick={() => Livewire.dispatch('map:list:show', {contentType: 'playlist', placeId: popupData.properties.id})}
                            disabled={!popupData.properties.contentCounts?.playlist}
                        >
                            <SvgPlaylist/>
                        </button>
                        <button
                            className="circle-button pos-3-5th"
                            title={popupData.properties.contentCounts?.podcast ? "Voir les podcasts" : "Aucun podcast"}
                            onClick={() => Livewire.dispatch('map:list:show', {contentType: 'podcast', placeId: popupData.properties.id})}
                            disabled={!popupData.properties.contentCounts?.podcast}
                        >
                            <SvgPodcast/>
                        </button>
                    </Popup>
                ) : (
                    <Popup
                        anchor="center"
                        offset={[0, 35] as Offset}
                        longitude={popupData.geometry.coordinates[0]}
                        latitude={popupData.geometry.coordinates[1]}
                        closeButton={false}
                        closeOnClick={false}
                        className="popup-content-solo"
                    >
                        <button className="popup-label"
                            onClick={() => Livewire.dispatchTo(
                                'router',
                                'nav:to',
                                {
                                    routeKey: 'place_details',
                                    params: {
                                        bindings: {
                                            place: {
                                                model: 'App\\Models\\Map\\Place',
                                                id: popupData.properties.id
                                            }
                                        }
                                    }
                                }
                            )}
                        >
                            {popupData.properties.label}
                        </button>
                    </Popup>
                )
            )}

            <div className="custom-control">
                <button
                    type="button"
                    className="btn btn-secondary d-flex"
                    title="Changer le fond de carte"
                    onClick={() => setShowOrtho(prevState => !prevState)}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                    >
                        <path d="m14.12 10.163 1.715.858c.22.11.22.424 0 .534L8.267 15.34a.6.6 0 0 1-.534 0L.165 11.555a.299.299 0 0 1 0-.534l1.716-.858 5.317 2.659c.505.252 1.1.252 1.604 0l5.317-2.66zM7.733.063a.6.6 0 0 1 .534 0l7.568 3.784a.3.3 0 0 1 0 .535L8.267 8.165a.6.6 0 0 1-.534 0L.165 4.382a.299.299 0 0 1 0-.535z"/>
                        <path d="m14.12 6.576 1.715.858c.22.11.22.424 0 .534l-7.568 3.784a.6.6 0 0 1-.534 0L.165 7.968a.299.299 0 0 1 0-.534l1.716-.858 5.317 2.659c.505.252 1.1.252 1.604 0z"/>
                    </svg>
                </button>
            </div>
        </ReactMap>
    );
};

export default SunMap;
